import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usabilityAdminService, nationsService, categoriesService, participantsService, groupsService, ageGroupsService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";
import ConfirmationModal from "./ConfirmationModal";
import HeaderTailwind from "../generic/HeaderTailwind";
import GenericSelectTailwind from "./GenericSelectTailwind";

function UsabilityTasksTailwind() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importData, setImportData] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    orderIndex: 0,
    expectedType: "INPUT_VALUE",
    expectedInputValue: "",
    expectedInputs: [{ title: "", value: "" }], // Array für mehrere Inputs
    expectedStateSpec: null,
    roleName: "admin",
    isActive: true
  });

  // State für Validatoren (Array von Validator-Konfigurationen)
  const [validators, setValidators] = useState([]);
  
  // Daten für Dropdowns
  const [nations, setNations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);

  useEffect(() => {
    loadTasks();
    loadNationsAndCategories();
  }, []);

  const loadNationsAndCategories = async () => {
    try {
      const [nationsResponse, categoriesResponse, participantsResponse, groupsResponse, ageGroupsResponse] = await Promise.all([
        nationsService.getAllNations(),
        categoriesService.getAllCategories(),
        participantsService.getAllParticipants(),
        groupsService.getAllGroups(),
        ageGroupsService.getAllAgeGroups()
      ]);
      
      if (nationsResponse.success) {
        setNations(nationsResponse.data || []);
      }
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data || []);
      }
      if (participantsResponse.success) {
        setParticipants(participantsResponse.data || []);
      }
      if (groupsResponse.success) {
        setGroups(groupsResponse.data || []);
      }
      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usabilityAdminService.getAllTasks();
      if (response.success) {
        const tasksData = Array.isArray(response.data) ? response.data : [];
        setTasks(tasksData);
      } else {
        setError(response.error || "Fehler beim Laden der Aufgaben");
        setTasks([]);
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Aufgaben");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTask(null);
    setFormData({
      title: "",
      description: "",
      orderIndex: 0,
      expectedType: "INPUT_VALUE",
      expectedInputValue: "",
      expectedInputs: [{ title: "", value: "" }],
      expectedStateSpec: null,
      roleName: "admin",
      isActive: true
    });
    setValidators([]);
    setShowModal(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    // Konvertiere expectedInputValue zu expectedInputs falls es ein String ist
    let expectedInputs = [{ title: "", value: "" }];
    if (task.expectedInputValue) {
      try {
        // Versuche es als JSON zu parsen (Array von Inputs)
        const parsed = JSON.parse(task.expectedInputValue);
        if (Array.isArray(parsed) && parsed.length > 0) {
          expectedInputs = parsed;
        } else if (typeof parsed === 'object' && parsed.title && parsed.value) {
          expectedInputs = [parsed];
        } else {
          // Fallback: Einfacher String
          expectedInputs = [{ title: "Eingabe", value: task.expectedInputValue }];
        }
      } catch {
        // Kein JSON, verwende als einfachen String
        expectedInputs = [{ title: "Eingabe", value: task.expectedInputValue }];
      }
    }
    setFormData({
      title: task.title,
      description: task.description || "",
      orderIndex: task.orderIndex,
      expectedType: task.expectedType,
      expectedInputValue: task.expectedInputValue || "",
      expectedInputs: expectedInputs,
      expectedStateSpec: task.expectedStateSpec || null,
      roleName: task.roleName,
      isActive: task.isActive
    });
    
    // Initialisiere Validatoren wenn expectedStateSpec vorhanden
    if (task.expectedType === "STATE_CHANGE" && task.expectedStateSpec) {
      // Prüfe ob es das neue Format (Array) oder altes Format (Objekt) ist
      if (Array.isArray(task.expectedStateSpec)) {
        setValidators(task.expectedStateSpec);
      } else {
        // Altes Format: Konvertiere zu neuem Format (ein participantGroupValidator)
        const spec = task.expectedStateSpec;
        const validator = {
          type: 'participantGroupValidator',
          params: {}
        };
        
        if (spec.venue) {
          validator.params.venue = spec.venue;
        }
        if (spec.status) {
          validator.params.status = spec.status;
        }
        if (spec.count) {
          if (spec.count.exact !== undefined) {
            validator.params.countType = 'exact';
            validator.params.countValue = spec.count.exact;
          } else if (spec.count.min !== undefined) {
            validator.params.countType = 'min';
            validator.params.countValue = spec.count.min;
          } else if (spec.count.max !== undefined) {
            validator.params.countType = 'max';
            validator.params.countValue = spec.count.max;
          }
        }
        
        setValidators([validator]);
      }
    } else {
      setValidators([]);
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (task) => {
    try {
      // Sende nur die benötigten Felder, nicht das gesamte Task-Objekt
      const taskData = {
        title: task.title,
        description: task.description || null,
        orderIndex: task.orderIndex || 0,
        expectedType: task.expectedType,
        roleName: task.roleName,
        isActive: !task.isActive
      };
      
      // Füge nur die relevanten Felder hinzu, abhängig vom expectedType
      if (task.expectedType === "INPUT_VALUE" && task.expectedInputValue) {
        taskData.expectedInputValue = task.expectedInputValue;
      } else if (task.expectedType === "STATE_CHANGE" && task.expectedStateSpec) {
        taskData.expectedStateSpec = task.expectedStateSpec;
      }
      
      const response = await usabilityAdminService.updateTask(task.id, taskData);
      if (response.success) {
        addToast("success", task.isActive ? "Aufgabe erfolgreich deaktiviert" : "Aufgabe erfolgreich aktiviert");
        loadTasks();
      } else {
        addToast("error", response.error);
      }
    } catch (err) {
      addToast("error", "Fehler beim Ändern des Status");
    }
  };

  const handleDelete = (task) => {
    setTaskToDelete(task);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      const response = await usabilityAdminService.deleteTask(taskToDelete.id);
      if (response.success) {
        addToast("success", "Aufgabe erfolgreich gelöscht");
        loadTasks();
      } else {
        addToast("error", response.error);
      }
    } catch (err) {
      addToast("error", "Fehler beim Löschen der Aufgabe");
    } finally {
      setShowConfirmation(false);
      setTaskToDelete(null);
    }
  };

  // Validator-Definitionen
  const VALIDATOR_TYPES = [
    { 
      value: 'participantValidator', 
      label: 'Teilnehmer-Validator',
      description: 'Prüft, ob ein Teilnehmer mit bestimmten Eigenschaften existiert',
      icon: 'fa-user'
    },
    { 
      value: 'participantGroupValidator', 
      label: 'Teilnehmer-Gruppen-Validator',
      description: 'Prüft, ob Teilnehmer in einer aktiven Gruppe auf einer Fläche anwesend sind',
      icon: 'fa-users'
    },
    { 
      value: 'categoryValidator', 
      label: 'Kategorie-Validator',
      description: 'Prüft, ob ein Teilnehmer bestimmte Kategorien hat',
      icon: 'fa-tags'
    },
    { 
      value: 'resultValidator', 
      label: 'Ergebnis-Validator',
      description: 'Prüft, ob ein Athlet in einer Gruppe ein Ergebnis (Punkte) hat',
      icon: 'fa-trophy'
    },
    { 
      value: 'scoreValidator', 
      label: 'Punkte-Validator',
      description: 'Prüft, ob die exakten Scores (J1-J5 und Penalty) für einen Teilnehmer in einer Gruppe den übergebenen Werten entsprechen',
      icon: 'fa-star'
    },
    { 
      value: 'categoryExistenceValidator', 
      label: 'Kategorie-Existenz-Validator',
      description: 'Prüft, ob eine spezielle Kategorie existiert (mit optionalem Namen und Altersgruppen)',
      icon: 'fa-check-circle'
    },
    { 
      value: 'startNumberValidator', 
      label: 'Startnummer-Validator',
      description: 'Prüft, ob ein Athlet in einer Gruppe eine bestimmte Startnummer hat',
      icon: 'fa-hashtag'
    },
    { 
      value: 'partnerTeamValidator', 
      label: 'Partnerteam-Validator',
      description: 'Prüft, ob ein Partnerteam mit zwei speziellen Athleten in einer der Partnergruppen existiert',
      icon: 'fa-handshake'
    },
    { 
      value: 'nationTeamValidator', 
      label: 'Nation-Team-Validator',
      description: 'Prüft, ob eine Nation ein Team für die Gruppenperformance registriert hat und validiert die Anzahl der Teilnehmer',
      icon: 'fa-users-cog'
    },
    { 
      value: 'scheduleValidator', 
      label: 'Zeitplan-Validator',
      description: 'Prüft, ob eine Gruppe beim Zeitplan an einem speziellen Tag auf einer speziellen Fläche stattfindet und der Index auf der Fläche an diesem Tag',
      icon: 'fa-calendar-alt'
    },
    { 
      value: 'groupStatusValidator', 
      label: 'Gruppen-Status-Validator',
      description: 'Prüft, ob eine Gruppe einen bestimmten Status hat (z.B. Abgeschlossen, Aktiv, Ausstehend, Vorbereitung)',
      icon: 'fa-check-double'
    }
  ];

  // Funktion zum Hinzufügen eines Validators
  const handleAddValidator = () => {
    setValidators([
      ...validators,
      {
        type: 'participantValidator',
        params: {}
      }
    ]);
  };

  // Funktion zum Entfernen eines Validators
  const handleRemoveValidator = (index) => {
    setValidators(validators.filter((_, i) => i !== index));
  };

  // Funktion zum Aktualisieren eines Validators
  const handleUpdateValidator = (index, field, value) => {
    const updated = [...validators];
    if (field === 'type') {
      // Wenn Typ geändert wird, setze params zurück
      updated[index] = { type: value, params: {} };
    } else {
      updated[index] = {
        ...updated[index],
        params: {
          ...updated[index].params,
          [field]: value
        }
      };
    }
    setValidators(updated);
  };

  // Funktion zum Konvertieren von Validatoren in expectedStateSpec
  const buildExpectedStateSpec = () => {
    if (validators.length === 0) {
      return null;
    }
    
    // Konvertiere Validatoren-Array zu erwartetem Format
    return validators.map(v => {
      // Entferne leere Parameter (null, undefined, leere Strings)
      const cleanedParams = {};
      Object.entries(v.params || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Für Arrays: nur hinzufügen wenn nicht leer
          if (Array.isArray(value) && value.length > 0) {
            cleanedParams[key] = value;
          } else if (!Array.isArray(value)) {
            cleanedParams[key] = value;
          }
        }
      });
      
      return {
        type: v.type,
        params: cleanedParams
      };
    });
  };

  // Validierungsfunktion
  const isFormValid = () => {
    // Titel ist Pflichtfeld
    if (!formData.title || formData.title.trim() === "") {
      return false;
    }
    
    // Aufgabenstellung ist Pflichtfeld
    if (!formData.description || formData.description.trim() === "") {
      return false;
    }
    
    // Typ ist Pflichtfeld
    if (!formData.expectedType) {
      return false;
    }
    
    // Rolle ist Pflichtfeld
    if (!formData.roleName) {
      return false;
    }
    
    // Bei INPUT_VALUE: Mindestens ein Input-Wert muss vorhanden sein
    if (formData.expectedType === "INPUT_VALUE") {
      const validInputs = formData.expectedInputs.filter(input => input.value && input.value.trim() !== "");
      if (validInputs.length === 0) {
        return false;
      }
    }
    
    // Bei STATE_CHANGE: Mindestens ein Validator muss vorhanden sein
    if (formData.expectedType === "STATE_CHANGE") {
      if (validators.length === 0) {
        return false;
      }
      // Jeder Validator muss einen Typ haben
      for (const validator of validators) {
        if (!validator.type) {
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    // Validiere Formular
    if (!isFormValid()) {
      addToast("error", "Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    
    try {
      // Validiere, dass mindestens ein Input-Wert vorhanden ist, wenn INPUT_VALUE
      if (formData.expectedType === "INPUT_VALUE") {
        const validInputs = formData.expectedInputs.filter(input => input.value && input.value.trim() !== "");
        if (validInputs.length === 0) {
          addToast("error", "Bitte geben Sie mindestens einen Eingabewert an");
          return;
        }
      }

      // Konvertiere expectedInputs zu expectedInputValue
      let expectedInputValue = "";
      if (formData.expectedType === "INPUT_VALUE") {
        const validInputs = formData.expectedInputs.filter(input => input.value && input.value.trim() !== "");
        if (validInputs.length === 1 && !validInputs[0].title) {
          // Einfacher Fall: Nur ein Input ohne Titel -> einfacher String
          expectedInputValue = validInputs[0].value.trim();
        } else {
          // Mehrere Inputs oder Inputs mit Titel -> JSON Array
          expectedInputValue = JSON.stringify(validInputs.map(input => ({
            title: input.title || "",
            value: input.value.trim()
          })));
        }
      }

      // Erstelle taskData ohne expectedInputs
      const taskData = {
        title: formData.title.trim(),
        description: formData.description ? formData.description.trim() : null,
        orderIndex: formData.orderIndex || 0,
        expectedType: formData.expectedType,
        roleName: formData.roleName,
        isActive: formData.isActive !== undefined ? formData.isActive : true
      };

      // Füge nur die relevanten Felder hinzu, abhängig vom expectedType
      if (formData.expectedType === "INPUT_VALUE") {
        taskData.expectedInputValue = expectedInputValue || "";
      } else if (formData.expectedType === "STATE_CHANGE") {
        // Verwende buildExpectedStateSpec() um expectedStateSpec zu erstellen
        const stateSpecData = buildExpectedStateSpec();
        if (stateSpecData) {
          taskData.expectedStateSpec = stateSpecData;
        } else {
          addToast("error", "Bitte fügen Sie mindestens einen Validator hinzu");
          return;
        }
      }

      let response;
      if (editingTask) {
        response = await usabilityAdminService.updateTask(editingTask.id, taskData);
      } else {
        response = await usabilityAdminService.createTask(taskData);
      }

      if (response.success) {
        addToast("success", editingTask ? "Aufgabe aktualisiert" : "Aufgabe erstellt");
        loadTasks();
        setShowModal(false);
      } else {
        addToast("error", response.error || "Fehler beim Speichern");
      }
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      addToast("error", "Fehler beim Speichern: " + (err.message || "Unbekannter Fehler"));
    }
  };

  const handleAddInput = () => {
    setFormData({
      ...formData,
      expectedInputs: [...formData.expectedInputs, { title: "", value: "" }]
    });
  };

  const handleRemoveInput = (index) => {
    if (formData.expectedInputs.length > 1) {
      setFormData({
        ...formData,
        expectedInputs: formData.expectedInputs.filter((_, i) => i !== index)
      });
    }
  };

  const handleInputChange = (index, field, value) => {
    const newInputs = [...formData.expectedInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setFormData({ ...formData, expectedInputs: newInputs });
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterRole("all");
    setFilterStatus("all");
  };

  const getActiveFilters = () => {
    const active = [];
    if (filterRole !== "all") {
      active.push({ type: "Rolle", value: filterRole, onRemove: () => setFilterRole("all") });
    }
    if (filterStatus !== "all") {
      const statusLabel = filterStatus === "active" ? "Aktiv" : "Inaktiv";
      active.push({ type: "Status", value: statusLabel, onRemove: () => setFilterStatus("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  const filteredTasks = tasks.filter(task => {
    // Suche nach Titel oder Beschreibung (case-insensitive)
    const matchesSearch = !searchTerm.trim() || 
      (task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       task.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    if (filterRole !== "all" && task.roleName !== filterRole) return false;
    if (filterStatus !== "all") {
      if (filterStatus === "active" && !task.isActive) return false;
      if (filterStatus === "inactive" && task.isActive) return false;
    }
    return true;
  });

  // Pagination Logic
  const totalItems = filteredTasks.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedTasks = itemsPerPage === "all" 
    ? filteredTasks 
    : filteredTasks.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExport = () => {
    try {
      const exportData = {
        version: "1.0",
        exportDate: new Date().toISOString(),
        tasks: filteredTasks.map(task => ({
          id: task.id,
          title: task.title,
          description: task.description,
          orderIndex: task.orderIndex,
          expectedType: task.expectedType,
          expectedInputValue: task.expectedInputValue,
          expectedStateSpec: task.expectedStateSpec,
          roleName: task.roleName,
          isActive: task.isActive
        }))
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usability-tasks-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      addToast("success", "Aufgaben erfolgreich exportiert");
    } catch (err) {
      addToast("error", "Fehler beim Exportieren der Aufgaben");
      console.error("Export error:", err);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      if (!importData.tasks || !Array.isArray(importData.tasks)) {
        addToast("error", "Ungültiges Dateiformat");
        return;
      }

      setImportData(importData.tasks);
      setShowImportConfirmation(true);
    } catch (err) {
      addToast("error", "Fehler beim Lesen der Datei");
      console.error("Import error:", err);
    } finally {
      // Reset file input
      event.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!importData) return;

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const taskData of importData) {
        try {
          // Prüfe ob Aufgabe bereits existiert
          // 1. Priorität: Suche nach ID (wenn vorhanden)
          // 2. Fallback: Suche nach Titel und Rolle (für alte Exporte ohne ID)
          const existingTask = taskData.id 
            ? tasks.find(t => t.id === taskData.id)
            : tasks.find(t => t.title === taskData.title && t.roleName === taskData.roleName);

          if (existingTask) {
            // Update existing task
            const response = await usabilityAdminService.updateTask(existingTask.id, taskData);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } else {
            // Create new task
            const response = await usabilityAdminService.createTask(taskData);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          }
        } catch (err) {
          errorCount++;
          console.error("Error importing task:", err);
        }
      }

      await loadTasks();
      addToast(
        successCount > 0 ? "success" : "error",
        `${successCount} Aufgabe(n) erfolgreich importiert${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`
      );
    } catch (err) {
      addToast("error", "Fehler beim Importieren der Aufgaben");
      console.error("Import error:", err);
    } finally {
      setLoading(false);
      setShowImportConfirmation(false);
      setImportData(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600 dark:text-slate-400">Lade Aufgaben...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-900 dark:text-slate-100 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadTasks} 
                  className="inline-flex items-center px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.usability.tasks.title", "Usability-Aufgaben verwalten")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.usability.tasks.subtitle", "Erstellen und verwalten Sie Aufgaben für Usability-Tests.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {filteredTasks.length}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {filteredTasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-6 flex items-center justify-end gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <i className="fas fa-download text-xs"></i>
              Exportieren
            </button>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer">
              <i className="fas fa-upload text-xs"></i>
              Importieren
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.usability.tasks.addTask", "Neue Aufgabe")}
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Aufgaben suchen..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Rolle Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  ROLLE
                </label>
                <GenericSelectTailwind
                  value={filterRole}
                  onChange={setFilterRole}
                  options={[
                    { value: "all", label: "Alle Rollen" },
                    { value: "admin", label: "Admin" },
                    { value: "scorekeeper", label: "Scorekeeper" },
                    { value: "caller", label: "Caller" },
                    { value: "superadmin", label: "Superadmin" },
                    { value: "user", label: "User (Zuschauer)" }
                  ]}
                  placeholder="Rolle auswählen..."
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  STATUS
                </label>
                <GenericSelectTailwind
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { value: "all", label: "Alle Status" },
                    { value: "active", label: "Aktiv" },
                    { value: "inactive", label: "Inaktiv" }
                  ]}
                  placeholder="Status auswählen..."
                />
              </div>
            </div>

            {/* Active Filters Section */}
            {getActiveFilters().length > 0 && (
              <>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Aktive Filter:
                    </h3>
                    <button
                      onClick={handleResetFilters}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <i className="fas fa-undo text-xs"></i>
                      Filter zurücksetzen
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getActiveFilters().map((filter, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      >
                        <span>{filter.type}: {filter.value}</span>
                        <button
                          onClick={filter.onRemove}
                          className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                        >
                          <i className="fas fa-times text-[10px]"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Toast */}
          {toast.show && (
            <div className="fixed top-4 right-4 z-50">
              <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
                toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
                toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
                'border-blue-200 dark:border-blue-800'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                    toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                    'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    <i className={`fas text-xs ${
                      toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                      toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                      'fa-info text-blue-600 dark:text-blue-400'
                    }`}></i>
                  </div>
                  <span className={`text-sm font-medium ${
                    toast.type === 'success' ? 'text-green-900 dark:text-green-100' :
                    toast.type === 'error' ? 'text-red-900 dark:text-red-100' :
                    'text-blue-900 dark:text-blue-100'
                  }`}>{toast.message}</span>
                  <button
                    onClick={hideToast}
                    className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Titel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Rolle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Reihenfolge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedTasks && paginatedTasks.length > 0 ? (
                    paginatedTasks.map((task) => (
                      <tr key={task.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-50">
                          {task.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                            {task.expectedType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {task.roleName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {task.orderIndex}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            task.isActive
                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-700/30 dark:border-green-300/30"
                              : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-700/30 dark:border-red-300/30"
                          }`}>
                            {task.isActive ? "Aktiv" : "Inaktiv"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(task)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light transition-colors"
                              title="Bearbeiten"
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleToggleActive(task)}
                              className={`p-2 transition-colors ${
                                task.isActive
                                  ? "text-slate-600 dark:text-slate-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                                  : "text-slate-600 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400"
                              }`}
                              title={task.isActive ? "Deaktivieren" : "Aktivieren"}
                            >
                              <i className={`fas ${task.isActive ? "fa-eye-slash" : "fa-eye"} text-xs`}></i>
                            </button>
                            <button
                              onClick={() => handleDelete(task)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Löschen"
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                            <i className="fas fa-tasks text-slate-400 dark:text-slate-500"></i>
                          </div>
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                            Keine Aufgaben gefunden
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                            {searchTerm || filterRole !== "all" || filterStatus !== "all"
                              ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                              : "Fügen Sie die erste Aufgabe hinzu, um zu beginnen."
                            }
                          </p>
                          {(!searchTerm && filterRole === "all" && filterStatus === "all") && (
                            <button
                              onClick={handleAdd}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                              <i className="fas fa-plus text-xs"></i>
                              Erste Aufgabe hinzufügen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Pagination Footer Row */}
                  {filteredTasks.length > 0 && (
                    <tr className="bg-slate-50 dark:bg-slate-700/30">
                      <td colSpan="6" className="px-6 py-4">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                          {/* Items per page selector */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige
                            </span>
                            <div className="relative" style={{ zIndex: showModal || showConfirmation ? 1 : 100, pointerEvents: showModal || showConfirmation ? 'none' : 'auto' }}>
                              <GenericSelectTailwind
                                value={itemsPerPage.toString()}
                                onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                                options={[
                                  { value: "10", label: "10" },
                                  { value: "20", label: "20" },
                                  { value: "50", label: "50" },
                                  { value: "100", label: "100" },
                                  { value: "all", label: "Alle" }
                                ]}
                                placeholder="10"
                                openUpward={true}
                                whiteBackground={true}
                                disabled={showModal || showConfirmation}
                              />
                            </div>
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Einträge pro Seite
                            </span>
                          </div>

                          {/* Pagination Info and Controls */}
                          <div className="flex items-center gap-4">
                            {/* Info Text */}
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Aufgaben
                            </span>

                            {/* Scroll to Top Button */}
                            <button
                              onClick={scrollToTop}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                              title="Nach oben scrollen"
                            >
                              <i className="fas fa-arrow-up text-xs"></i>
                            </button>

                            {/* Pagination Buttons */}
                            {itemsPerPage !== "all" && totalPages > 1 && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-left text-xs"></i>
                                </button>

                                {/* Page Numbers */}
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${
                                        currentPage === pageNum
                                          ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-right text-xs"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal für Task-Formular */}
          {showModal && (
            <AdminModalTailwind
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              title={editingTask ? t("admin.usability.tasks.editTask", "Aufgabe bearbeiten") : t("admin.usability.tasks.newTask", "Neue Aufgabe erstellen")}
              subtitle={editingTask ? t("admin.usability.tasks.editSubtitle", "Bearbeiten Sie die Details der Aufgabe.") : t("admin.usability.tasks.newSubtitle", "Definieren Sie die Details für den neuen Usability-Test.")}
              onSave={handleSave}
              saveButtonText={t("admin.usability.tasks.saveTask", "Aufgabe speichern")}
              cancelButtonText={t("admin.usability.tasks.cancel", "Abbrechen")}
              isSaveDisabled={!isFormValid()}
            >
              <div className="space-y-6">
                {/* BASISINFORMATIONEN */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-info-circle text-slate-500 dark:text-slate-400"></i>
                      BASISINFORMATIONEN
                    </h3>
                  </div>
                  <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                        {t("admin.usability.tasks.title", "Titel")} *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="z.B. Navigationsmenü finden"
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                        {t("admin.usability.tasks.taskDescription", "Aufgabenstellung")} *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={4}
                        placeholder="Beschreiben Sie die Aufgabe, die der Nutzer ausführen soll..."
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                        required
                      />
                    </div>
                  </div>
                </div>
                </div>

                {/* KONFIGURATION */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-cog text-slate-500 dark:text-slate-400"></i>
                      KONFIGURATION
                    </h3>
                  </div>
                  <div className="p-6 overflow-visible">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                        {t("admin.usability.tasks.type", "Typ")} *
                      </label>
                      <div className="relative">
                        <GenericSelectTailwind
                          value={formData.expectedType}
                          onChange={(value) => setFormData({ ...formData, expectedType: value })}
                          options={[
                            { value: "INPUT_VALUE", label: "Eingabewert (INPUT_VALUE)" },
                            { value: "STATE_CHANGE", label: "Statusänderung (STATE_CHANGE)" }
                          ]}
                          placeholder="Typ auswählen..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                        {t("admin.usability.tasks.role", "Rolle")} *
                      </label>
                      <div className="relative">
                        <GenericSelectTailwind
                          value={formData.roleName}
                          onChange={(value) => setFormData({ ...formData, roleName: value })}
                          options={[
                            { value: "admin", label: "Administrator" },
                            { value: "scorekeeper", label: "Scorekeeper" },
                            { value: "caller", label: "Caller" },
                            { value: "superadmin", label: "Superadmin" },
                            { value: "user", label: "User (Zuschauer)" }
                          ]}
                          placeholder="Rolle auswählen..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                </div>

                {/* ERWARTETE EINGABEWERTE */}
                {formData.expectedType === "INPUT_VALUE" && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                          <i className="fas fa-list text-slate-500 dark:text-slate-400"></i>
                          ERWARTETE EINGABEWERTE
                        </h3>
                        <button
                          type="button"
                          onClick={handleAddInput}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <i className="fas fa-plus text-xs"></i>
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                    <div className="p-6 overflow-visible">
                    
                    {/* Bestehende Inputs als Chips - nur anzeigen wenn es bereits gespeicherte gibt */}
                    {formData.expectedInputs.filter(input => input.value && input.value.trim() !== "").length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {formData.expectedInputs
                          .map((input, index) => {
                            if (!input.value || input.value.trim() === "") return null;
                            return (
                              <div
                                key={index}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600"
                              >
                                <span>
                                  {input.title ? `${input.title}: ${input.value}` : input.value}
                                </span>
                                {formData.expectedInputs.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveInput(index)}
                                    className="ml-1 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                  >
                                    <i className="fas fa-times text-[10px]"></i>
                                  </button>
                                )}
                              </div>
                            );
                          })
                          .filter(Boolean)}
                      </div>
                    )}

                    {/* Neue Input-Felder */}
                    <div className="space-y-3">
                      {formData.expectedInputs.map((input, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                              Titel (optional)
                            </label>
                            <input
                              type="text"
                              value={input.title}
                              onChange={(e) => handleInputChange(index, "title", e.target.value)}
                              placeholder="z.B. Vorname"
                              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                Wert
                              </label>
                              <input
                                type="text"
                                value={input.value}
                                onChange={(e) => handleInputChange(index, "value", e.target.value)}
                                placeholder="z.B. Max"
                                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                              />
                            </div>
                            {formData.expectedInputs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveInput(index)}
                                className="mb-0.5 p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                title={t("admin.usability.tasks.removeInput", "Input entfernen")}
                              >
                                <i className="fas fa-trash text-sm"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                      <i className="fas fa-info-circle mr-1"></i>
                      {t("admin.usability.tasks.inputsHint", "Sie können mehrere Eingabewerte definieren. Wenn kein Titel angegeben wird, wird nur der Wert verwendet.")}
                    </p>
                    </div>
                  </div>
                )}

                {/* STATE-VALIDIERUNG */}
                {formData.expectedType === "STATE_CHANGE" && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                      <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                          <i className="fas fa-database text-slate-500 dark:text-slate-400"></i>
                          STATE-VALIDIERUNG
                      </h3>
                        <button
                          type="button"
                          onClick={handleAddValidator}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary/90 dark:hover:bg-primary-light/90 transition-colors"
                        >
                          <i className="fas fa-plus text-xs"></i>
                          Validator hinzufügen
                        </button>
                    </div>
                    </div>
                    <div className="p-6 space-y-4">
                      {validators.length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400 italic text-center py-8">
                          Keine Validatoren hinzugefügt. Klicken Sie auf "Validator hinzufügen", um einen Validator zu konfigurieren.
                        </p>
                      ) : (
                        validators.map((validator, index) => {
                          const validatorType = VALIDATOR_TYPES.find(vt => vt.value === validator.type);
                          return (
                            <div key={index} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 p-4">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <i className={`fas ${validatorType?.icon || 'fa-check-circle'} text-slate-500 dark:text-slate-400`}></i>
                                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                      {validatorType?.label || 'Unbekannter Validator'}
                                    </h4>
                                  </div>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {validatorType?.description || ''}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveValidator(index)}
                                  className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                  title="Validator entfernen"
                                >
                                  <i className="fas fa-trash text-sm"></i>
                                </button>
                              </div>
                              
                              {/* Validator-Typ auswählen */}
                              <div className="mb-4">
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                  Validator-Typ *
                                </label>
                                <GenericSelectTailwind
                                  value={validator.type}
                                  onChange={(value) => handleUpdateValidator(index, 'type', value)}
                                  options={VALIDATOR_TYPES.map(vt => ({ value: vt.value, label: vt.label }))}
                                  placeholder="Validator auswählen..."
                                  whiteBackground={true}
                                />
                              </div>
                              
                              {/* Parameter je nach Validator-Typ */}
                              {validator.type === 'participantValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Name (optional)
                      </label>
                                      <input
                                        type="text"
                                        value={validator.params.name || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'name', e.target.value)}
                                        placeholder="z.B. Max Mustermann"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Alter (optional)
                                      </label>
                                      <input
                                        type="number"
                                        min="8"
                                        max="100"
                                        value={validator.params.age || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'age', e.target.value)}
                                        placeholder="z.B. 25"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Geschlecht (optional)
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.gender || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'gender', value)}
                                        options={[
                                          { value: '', label: 'Alle' },
                                          { value: 'männlich', label: 'Männlich' },
                                          { value: 'weiblich', label: 'Weiblich' }
                                        ]}
                                        placeholder="Geschlecht auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Startnummer (optional)
                                      </label>
                                      <input
                                        type="number"
                                        min="100"
                                        value={validator.params.participantNumber || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'participantNumber', e.target.value)}
                                        placeholder="z.B. 101"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                  </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Nation (optional)
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.nationId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'nationId', value)}
                                        options={[
                                          { value: '', label: 'Alle' },
                                          ...nations.map(n => ({ value: String(n.id), label: n.name }))
                                        ]}
                                        placeholder="Nation auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                </div>
                              )}
                              
                              {validator.type === 'participantGroupValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Fläche *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.venue || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'venue', value)}
                                        options={[
                                          { value: '', label: 'Fläche auswählen...' },
                                          { value: 'Fläche A', label: 'Fläche A' },
                                          { value: 'Fläche B', label: 'Fläche B' }
                                        ]}
                                        placeholder="Fläche auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Status (optional)
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.status || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'status', value)}
                                        options={[
                                          { value: '', label: 'Alle' },
                                          { value: 'Aktiv', label: 'Aktiv' },
                                          { value: 'Ausstehend', label: 'Ausstehend' },
                                          { value: 'Abgeschlossen', label: 'Abgeschlossen' },
                                          { value: 'Vorbereitung', label: 'Vorbereitung' }
                                        ]}
                                        placeholder="Status auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Anzahl-Prüfung
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.countType || 'any'}
                                        onChange={(value) => handleUpdateValidator(index, 'countType', value)}
                                        options={[
                                          { value: 'any', label: 'Mindestens 1' },
                                          { value: 'min', label: 'Mindestens' },
                                          { value: 'max', label: 'Höchstens' },
                                          { value: 'exact', label: 'Genau' }
                                        ]}
                                        placeholder="Anzahl-Prüfung auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    {(validator.params.countType && validator.params.countType !== 'any') && (
                                      <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                          Anzahl
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          value={validator.params.countValue || ''}
                                          onChange={(e) => handleUpdateValidator(index, 'countValue', e.target.value)}
                                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'categoryValidator' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Teilnehmer-Name (optional)
                                    </label>
                                    <input
                                      type="text"
                                      value={validator.params.participantName || ''}
                                      onChange={(e) => handleUpdateValidator(index, 'participantName', e.target.value)}
                                      placeholder="z.B. Max Mustermann"
                                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                    />
                                  </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Kategorien (optional)
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.categoryIds ? (Array.isArray(validator.params.categoryIds) ? validator.params.categoryIds[0] : validator.params.categoryIds) : ''}
                                        onChange={(value) => handleUpdateValidator(index, 'categoryIds', value ? [value] : [])}
                                        options={[
                                          { value: '', label: 'Alle' },
                                          ...categories.map(c => ({ value: String(c.id), label: c.name }))
                                        ]}
                                        placeholder="Kategorie auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Anzahl-Prüfung
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.countType || 'any'}
                                        onChange={(value) => handleUpdateValidator(index, 'countType', value)}
                                        options={[
                                          { value: 'any', label: 'Mindestens 1' },
                                          { value: 'min', label: 'Mindestens' },
                                          { value: 'max', label: 'Höchstens' },
                                          { value: 'exact', label: 'Genau' }
                                        ]}
                                        placeholder="Anzahl-Prüfung auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    {(validator.params.countType && validator.params.countType !== 'any') && (
                                      <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                          Anzahl
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          value={validator.params.countValue || ''}
                                          onChange={(e) => handleUpdateValidator(index, 'countValue', e.target.value)}
                                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'resultValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Teilnehmer *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.participantId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'participantId', value)}
                                        options={[
                                          { value: '', label: 'Teilnehmer auswählen...' },
                                          ...participants.map(p => ({ value: String(p.id), label: `${p.name} (#${p.participantNumber})` }))
                                        ]}
                                        placeholder="Teilnehmer auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Gruppe *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.groupId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                        options={[
                                          { value: '', label: 'Gruppe auswählen...' },
                                          ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                        ]}
                                        placeholder="Gruppe auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'scoreValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Teilnehmer *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.participantId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'participantId', value)}
                                        options={[
                                          { value: '', label: 'Teilnehmer auswählen...' },
                                          ...participants.map(p => ({ value: String(p.id), label: `${p.name} (#${p.participantNumber})` }))
                                        ]}
                                        placeholder="Teilnehmer auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Gruppe *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.groupId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                        options={[
                                          { value: '', label: 'Gruppe auswählen...' },
                                          ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                        ]}
                                        placeholder="Gruppe auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        J1 (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        value={validator.params.j1 || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'j1', e.target.value)}
                                        placeholder="0.00 - 10.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        J2 (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        value={validator.params.j2 || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'j2', e.target.value)}
                                        placeholder="0.00 - 10.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        J3 (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        value={validator.params.j3 || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'j3', e.target.value)}
                                        placeholder="0.00 - 10.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        J4 (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        value={validator.params.j4 || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'j4', e.target.value)}
                                        placeholder="0.00 - 10.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        J5 (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        value={validator.params.j5 || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'j5', e.target.value)}
                                        placeholder="0.00 - 10.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Penalty (optional)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="-2"
                                        max="2"
                                        value={validator.params.penalty || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'penalty', e.target.value)}
                                        placeholder="-2.00 - 2.00"
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'categoryExistenceValidator' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Kategorie-Name (optional)
                                    </label>
                                    <input
                                      type="text"
                                      value={validator.params.name || ''}
                                      onChange={(e) => handleUpdateValidator(index, 'name', e.target.value)}
                                      placeholder="z.B. Handform"
                                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Altersgruppen (optional)
                                    </label>
                                    <GenericSelectTailwind
                                      value={Array.isArray(validator.params.ageGroupIds) ? validator.params.ageGroupIds[0] || '' : ''}
                                      onChange={(value) => {
                                        const currentIds = Array.isArray(validator.params.ageGroupIds) ? validator.params.ageGroupIds : [];
                                        if (value) {
                                          if (!currentIds.includes(value)) {
                                            handleUpdateValidator(index, 'ageGroupIds', [...currentIds, value]);
                                          }
                                        }
                                      }}
                                      options={[
                                        { value: '', label: 'Altersgruppe auswählen...' },
                                        ...ageGroups.map(ag => ({ value: String(ag.id), label: ag.name }))
                                      ]}
                                      placeholder="Altersgruppe auswählen..."
                                      whiteBackground={true}
                                    />
                                    {Array.isArray(validator.params.ageGroupIds) && validator.params.ageGroupIds.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {validator.params.ageGroupIds.map((id, idx) => {
                                          const ageGroup = ageGroups.find(ag => String(ag.id) === String(id));
                                          return ageGroup ? (
                                            <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light rounded text-xs">
                                              {ageGroup.name}
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newIds = validator.params.ageGroupIds.filter(i => String(i) !== String(id));
                                                  handleUpdateValidator(index, 'ageGroupIds', newIds);
                                                }}
                                                className="hover:text-red-600 dark:hover:text-red-400"
                                              >
                                                ×
                                              </button>
                                            </span>
                                          ) : null;
                                        })}
                    </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'startNumberValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Teilnehmer *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.participantId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'participantId', value)}
                                        options={[
                                          { value: '', label: 'Teilnehmer auswählen...' },
                                          ...participants.map(p => ({ value: String(p.id), label: `${p.name} (#${p.participantNumber})` }))
                                        ]}
                                        placeholder="Teilnehmer auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Gruppe *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.groupId || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                        options={[
                                          { value: '', label: 'Gruppe auswählen...' },
                                          ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                        ]}
                                        placeholder="Gruppe auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Startnummer *
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={validator.params.startNumber || ''}
                                      onChange={(e) => handleUpdateValidator(index, 'startNumber', e.target.value)}
                                      placeholder="z.B. 3"
                                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'partnerTeamValidator' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Teilnehmer 1 *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.participant1Id || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'participant1Id', value)}
                                        options={[
                                          { value: '', label: 'Teilnehmer auswählen...' },
                                          ...participants.map(p => ({ value: String(p.id), label: `${p.name} (#${p.participantNumber})` }))
                                        ]}
                                        placeholder="Teilnehmer 1 auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Teilnehmer 2 *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.participant2Id || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'participant2Id', value)}
                                        options={[
                                          { value: '', label: 'Teilnehmer auswählen...' },
                                          ...participants.map(p => ({ value: String(p.id), label: `${p.name} (#${p.participantNumber})` }))
                                        ]}
                                        placeholder="Teilnehmer 2 auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Gruppe (optional)
                                    </label>
                                    <GenericSelectTailwind
                                      value={validator.params.groupId || ''}
                                      onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                      options={[
                                        { value: '', label: 'Alle Gruppen' },
                                        ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                      ]}
                                      placeholder="Gruppe auswählen..."
                                      whiteBackground={true}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'nationTeamValidator' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Nation *
                                    </label>
                                    <GenericSelectTailwind
                                      value={validator.params.nationId || ''}
                                      onChange={(value) => handleUpdateValidator(index, 'nationId', value)}
                                      options={[
                                        { value: '', label: 'Nation auswählen...' },
                                        ...nations.map(n => ({ value: String(n.id), label: n.name }))
                                      ]}
                                      placeholder="Nation auswählen..."
                                      whiteBackground={true}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Anzahl Teilnehmer (optional)
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={validator.params.participantCount || ''}
                                      onChange={(e) => handleUpdateValidator(index, 'participantCount', e.target.value)}
                                      placeholder="z.B. 5"
                                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'scheduleValidator' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Gruppe *
                                    </label>
                                    <GenericSelectTailwind
                                      value={validator.params.groupId || ''}
                                      onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                      options={[
                                        { value: '', label: 'Gruppe auswählen...' },
                                        ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                      ]}
                                      placeholder="Gruppe auswählen..."
                                      whiteBackground={true}
                                    />
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Datum *
                                      </label>
                                      <input
                                        type="date"
                                        value={validator.params.date || ''}
                                        onChange={(e) => handleUpdateValidator(index, 'date', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                        Fläche *
                                      </label>
                                      <GenericSelectTailwind
                                        value={validator.params.venue || ''}
                                        onChange={(value) => handleUpdateValidator(index, 'venue', value)}
                                        options={[
                                          { value: '', label: 'Fläche auswählen...' },
                                          { value: 'Fläche A', label: 'Fläche A' },
                                          { value: 'Fläche B', label: 'Fläche B' }
                                        ]}
                                        placeholder="Fläche auswählen..."
                                        whiteBackground={true}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Index auf Fläche *
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={validator.params.sequenceNumber || ''}
                                      onChange={(e) => handleUpdateValidator(index, 'sequenceNumber', e.target.value)}
                                      placeholder="z.B. 3"
                                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {validator.type === 'groupStatusValidator' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Gruppe *
                                    </label>
                                    <GenericSelectTailwind
                                      value={validator.params.groupId || ''}
                                      onChange={(value) => handleUpdateValidator(index, 'groupId', value)}
                                      options={[
                                        { value: '', label: 'Gruppe auswählen...' },
                                        ...groups.map(g => ({ value: String(g.id), label: g.name }))
                                      ]}
                                      placeholder="Gruppe auswählen..."
                                      whiteBackground={true}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                                      Status *
                                    </label>
                                    <GenericSelectTailwind
                                      value={validator.params.status || ''}
                                      onChange={(value) => handleUpdateValidator(index, 'status', value)}
                                      options={[
                                        { value: '', label: 'Status auswählen...' },
                                        { value: 'Aktiv', label: 'Aktiv' },
                                        { value: 'Ausstehend', label: 'Ausstehend' },
                                        { value: 'Abgeschlossen', label: 'Abgeschlossen' },
                                        { value: 'Vorbereitung', label: 'Vorbereitung' }
                                      ]}
                                      placeholder="Status auswählen..."
                                      whiteBackground={true}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                      
                      {/* JSON-Vorschau */}
                      {validators.length > 0 && (
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                            Vorschau (JSON)
                          </label>
                          <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600">
                            <pre className="text-xs font-mono overflow-auto text-slate-700 dark:text-slate-300 max-h-40 whitespace-pre-wrap">
                              {JSON.stringify(buildExpectedStateSpec(), null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
                )}

                {/* WEITERE EINSTELLUNGEN */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-sliders-h text-slate-500 dark:text-slate-400"></i>
                      WEITERE EINSTELLUNGEN
                    </h3>
                  </div>
                  <div className="p-6 overflow-visible">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          {t("admin.usability.tasks.order", "Reihenfolge")}
                        </label>
                        <input
                          type="number"
                          value={formData.orderIndex}
                          onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          {t("admin.usability.tasks.active", "Aktiv")}
                        </label>
                        <div className="flex items-center">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.isActive}
                              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 dark:peer-focus:ring-primary-light/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary dark:peer-checked:bg-primary-light"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
               
                </div>
              </AdminModalTailwind>
            )}

          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => {
              setShowConfirmation(false);
              setTaskToDelete(null);
            }}
            onConfirm={handleConfirmDelete}
            title="Aufgabe löschen"
            subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
            message={taskToDelete ? `Möchten Sie die Aufgabe "${taskToDelete.title}" wirklich löschen?` : "Möchten Sie diese Aufgabe wirklich löschen?"}
            confirmText="Löschen"
            cancelText="Abbrechen"
            type="danger"
            layout="inline"
          />

          <ConfirmationModal
            isOpen={showImportConfirmation}
            onClose={() => {
              setShowImportConfirmation(false);
              setImportData(null);
            }}
            onConfirm={handleConfirmImport}
            title="Aufgaben importieren"
            message={importData ? `Möchten Sie ${importData.length} Aufgabe(n) importieren? Bestehende Aufgaben mit gleichem Titel und Rolle werden aktualisiert.` : "Möchten Sie die Aufgaben importieren?"}
            confirmText="Importieren"
            cancelText="Abbrechen"
            type="info"
          />
        </div>
      </main>
    </div>
  );
}

export default UsabilityTasksTailwind;
